import type { DataConnection as BaseDataConnection } from "../hooks/useDataConnections"

/** Extended DataConnection with parent asset metadata (added in task 10.1) */
export type DataConnection = BaseDataConnection & {
  assetId: string | null
  parentAssetId: string | null
}

export interface ParentGroupChild {
  assetId: string
  assetName: string
  monitoringStatus: string
  assetKind?: string | null
  volume?: number | null
  minPressure?: number | null
}

export interface ParentGroup {
  parentAssetId: string
  parentAssetName: string
  parentMiId: string | null
  connections: DataConnection[]
  children: ParentGroupChild[]
  activeCount: number
  totalMappedCount: number
}

export interface GroupedConnections {
  parentGroups: ParentGroup[]
  standaloneConnections: DataConnection[]
}

/** Asset metadata used to enrich ParentGroupChild records during grouping. */
export interface AssetInfoEntry {
  kind?: string | null
  volume?: number | null
  minPressure?: number | null
}

/**
 * Group connections by parent asset.
 * Connections whose mapped asset has a parent are grouped under a ParentGroup.
 * Connections with standalone assets (no parent) remain in the flat list.
 *
 * A connection that directly maps to a parent asset (its assetId equals a parentAssetId
 * used by other connections) is included in that parent's group as the "parent MI".
 *
 * @param assetInfoMap - Optional map of assetId → asset metadata (kind, volume, minPressure).
 *   When provided, children are enriched with assetKind, volume, and minPressure fields.
 */
export function groupConnectionsByParent(
  connections: DataConnection[],
  assetParentMap: Record<string, string | null>,
  assetNames: Record<string, string>,
  assetInfoMap?: Record<string, AssetInfoEntry>,
): GroupedConnections {
  const groupMap = new Map<string, DataConnection[]>()
  const standaloneConnections: DataConnection[] = []

  // Resolve parent asset ID for a connection, using the connection field first
  // and falling back to the assetParentMap lookup
  const resolveParentAssetId = (conn: DataConnection): string | null => {
    if (conn.parentAssetId) return conn.parentAssetId
    if (conn.assetId && assetParentMap[conn.assetId]) return assetParentMap[conn.assetId]
    return null
  }

  // Collect all parentAssetIds referenced by child connections
  const parentAssetIds = new Set<string>()
  for (const conn of connections) {
    const parentId = resolveParentAssetId(conn)
    if (parentId) {
      parentAssetIds.add(parentId)
    }
  }

  // First pass: categorize connections
  for (const conn of connections) {
    const parentId = resolveParentAssetId(conn)

    if (parentId) {
      // This connection's asset has a parent → group under parent
      const group = groupMap.get(parentId)
      if (group) {
        group.push(conn)
      } else {
        groupMap.set(parentId, [conn])
      }
    } else if (conn.assetId && parentAssetIds.has(conn.assetId)) {
      // This connection maps directly to a parent asset → include in that group
      const group = groupMap.get(conn.assetId)
      if (group) {
        group.push(conn)
      } else {
        groupMap.set(conn.assetId, [conn])
      }
    } else {
      // Standalone connection
      standaloneConnections.push(conn)
    }
  }

  // Second pass: build ParentGroup objects
  const parentGroups: ParentGroup[] = []

  for (const [parentAssetId, groupConnections] of groupMap) {
    // Find the parent MI: a connection whose assetId matches the parentAssetId
    const parentMi = groupConnections.find((c) => c.assetId === parentAssetId)
    const parentMiId = parentMi?.id ?? null

    const activeCount = groupConnections.filter((c) => c.status === "active").length
    const totalMappedCount = groupConnections.length

    parentGroups.push({
      parentAssetId,
      parentAssetName: assetNames[parentAssetId] ?? parentAssetId,
      parentMiId,
      connections: groupConnections,
      children: groupConnections
        .filter((c) => c.assetId && c.assetId !== parentAssetId)
        .map((c) => {
          const info = assetInfoMap?.[c.assetId!]
          return {
            assetId: c.assetId!,
            assetName: assetNames[c.assetId!] ?? c.assetId!,
            monitoringStatus: c.status,
            assetKind: info?.kind ?? null,
            volume: info?.volume ?? null,
            minPressure: info?.minPressure ?? null,
          }
        }),
      activeCount,
      totalMappedCount,
    })
  }

  return { parentGroups, standaloneConnections }
}

/** Asset Group membership, as returned by getSystemAssetGroups. */
export interface AssetGroupMembership {
  parentAssetId: string
  children: Array<{ assetId: string }>
}

/**
 * Connections to render as standalone cards: everything not already shown
 * inside a ParentGroupCard.
 *
 * @param parentNodeMonitoringInstanceIds - Monitoring Instance ids carried by
 *   the open assignments of Asset Group parents (system view nodes with child
 *   assets). Core gives each Asset Group's parent a placeholder Monitoring
 *   Instance with no holder Asset and no Point Mappings; it has nothing to
 *   manage, so it is hidden. A parent can also carry a simple Asset's own
 *   connection, which must stay, hence the assetOwned and assetId checks.
 */
export function selectUngroupedConnections(
  connections: DataConnection[],
  parentGroups: ParentGroup[],
  assetGroups: AssetGroupMembership[],
  parentNodeMonitoringInstanceIds: Set<string>,
): DataConnection[] {
  // Collect IDs of connections that are rendered inside parent groups
  const groupedConnectionIds = new Set<string>()
  for (const group of parentGroups) {
    for (const conn of group.connections) {
      groupedConnectionIds.add(conn.id)
    }
  }

  // Build set of parent asset IDs referenced by child connections or asset groups
  const parentAssetIdSet = new Set(
    connections.filter((c) => c.parentAssetId).map((c) => c.parentAssetId!)
  )
  for (const group of assetGroups) {
    parentAssetIdSet.add(group.parentAssetId)
  }

  // Build set of child asset IDs from asset groups
  const groupChildAssetIds = new Set<string>()
  for (const group of assetGroups) {
    for (const child of group.children) {
      groupChildAssetIds.add(child.assetId)
    }
  }

  // Exclude:
  // 1. Connections already rendered in a parent group card
  // 2. Child connections (have parentAssetId) - shown in group cards
  // 3. Parent MI connections (assetId matches a parentAssetId) - shown in group cards
  // 4. Connections whose assetId is a child of a known asset group
  // 5. Parent group placeholders (carried by a parent, no holder, no mappings)
  // TODO: Blocked on core change — see https://github.com/hydrologiq/core/issues/185
  return connections.filter(
    (c) =>
      !groupedConnectionIds.has(c.id) &&
      !c.parentAssetId &&
      !(c.assetId && parentAssetIdSet.has(c.assetId)) &&
      !(c.assetId && groupChildAssetIds.has(c.assetId)) &&
      !(parentNodeMonitoringInstanceIds.has(c.id) && !c.assetOwned && !c.assetId)
  )
}

/**
 * Assets whose connections sit inside a ParentGroupCard (the Modules). Those
 * cards never render a mapping panel, so standalone cards must be told these
 * Assets are already mapped elsewhere.
 */
export function groupedAssetIds(parentGroups: ParentGroup[]): Set<string> {
  const ids = new Set<string>()
  for (const group of parentGroups) {
    for (const conn of group.connections) {
      if (conn.assetId) ids.add(conn.assetId)
    }
  }
  return ids
}
